import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './ConfigPage.css';

function ConsensusConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const data = await adminAPI.getConsensusConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to fetch consensus config:', err);
      setError(err.response?.data?.detail || 'Failed to load consensus configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleThresholdChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [field]: parseFloat(value) || 0
      }
    }));
    setHasChanges(true);
  };

  const handleWeightChange = (provider, value) => {
    setConfig(prev => ({
      ...prev,
      provider_weights: {
        ...prev.provider_weights,
        [provider]: parseFloat(value) || 1.0
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await adminAPI.updateConsensusConfig(config);
      setConfig(updated);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save consensus config:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="config-page">
        <div className="page-header">
          <h1 className="page-title">Consensus Configuration</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading consensus configuration...</span>
        </div>
      </div>
    );
  }

  const thresholdFields = [
    { key: 'similarity_threshold', label: 'Similarity Threshold', hint: 'Semantic similarity for claim agreement (0.7 = moderately similar)' },
    { key: 'low_agreement_threshold', label: 'Low Agreement Threshold', hint: 'Below this ratio = potential disagreement (0.76)' },
    { key: 'high_disagreement_threshold', label: 'High Disagreement Threshold', hint: 'Below this ratio = major disagreement (0.51)' },
    { key: 'majority_vote_threshold', label: 'Majority Vote Threshold', hint: 'Minimum agreement for majority voting (0.5)' },
    { key: 'cascade_high_threshold', label: 'Cascade High Threshold', hint: 'Strict agreement threshold for cascade voting (0.8)' },
    { key: 'cascade_medium_threshold', label: 'Cascade Medium Threshold', hint: 'Moderate threshold for cascade voting (0.6)' },
    { key: 'contradiction_threshold', label: 'Contradiction Threshold', hint: 'Below this = claim is contradicted (0.3)' }
  ];

  const providers = [
    { key: 'anthropic', label: 'Anthropic (Claude)' },
    { key: 'openai', label: 'OpenAI (GPT)' },
    { key: 'google', label: 'Google (Gemini)' },
    { key: 'groq', label: 'Groq (Llama)' },
    { key: 'perplexity', label: 'Perplexity' },
    { key: 'xai', label: 'xAI (Grok)' },
    { key: 'mistral', label: 'Mistral' }
  ];

  return (
    <div className="config-page">
      <div className="page-header">
        <h1 className="page-title">Consensus Configuration</h1>
        <p className="page-subtitle">
          Configure how the consensus engine analyzes and weighs responses from multiple LLM providers.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {hasChanges && (
        <div className="save-bar">
          <span>You have unsaved changes</span>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      <div className="config-section">
        <h2>Consensus Thresholds</h2>
        <p className="section-desc">
          These thresholds control how the consensus engine identifies agreements and disagreements between providers.
        </p>
        <div className="threshold-grid">
          {thresholdFields.map(field => (
            <div key={field.key} className="field-group">
              <label>{field.label}</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={config?.thresholds?.[field.key] ?? 0}
                onChange={(e) => handleThresholdChange(field.key, e.target.value)}
              />
              <span className="field-hint">{field.hint}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="config-section">
        <h2>Provider Weights</h2>
        <p className="section-desc">
          Adjust the relative weight of each provider in weighted consensus voting. Higher weights give more influence.
        </p>
        <div className="weights-grid">
          {providers.map(provider => (
            <div key={provider.key} className="weight-item">
              <span className="provider-label">{provider.label}</span>
              <input
                type="number"
                min="0.1"
                max="2.0"
                step="0.05"
                value={config?.provider_weights?.[provider.key] ?? 1.0}
                onChange={(e) => handleWeightChange(provider.key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <p className="info-note">
          Default weight is 1.0. Values above 1.0 increase influence, below 1.0 decrease it.
        </p>
      </div>
    </div>
  );
}

export default ConsensusConfig;
